import './ValueFormatters.scss';
import { prefixClass } from 'lib/utils';
import classNames from 'classnames';
import { Tooltip, Whisper } from 'rsuite';
import { AuditResult } from 'types/report';

const prefix = prefixClass('value-formatters');

export const formatValue = (amount: number, decimals: number = 0, locale: string = 'en-US') => {
  return new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
};

export const formatAmountCondensed = (amount: number | null, zero = false) => {
  if (amount === null) return ' ';
  if (amount === 0) return zero ? '0' : '--';

  // Millions
  if (Math.abs(amount) >= 1000000) {
    let formatted = formatValue(Math.abs(amount) / 1000000, 1);
    if (formatted.indexOf('.0') > -1) formatted = formatted.replace('.0', '');
    return amount >= 0 ? `$${formatted}M` : `($${formatted}M)`;
  }
  //Thousands
  if (Math.abs(amount) >= 1000) {
    let formatted = formatValue(Math.abs(amount) / 1000, 1);
    if (formatted.indexOf('.0') > -1) formatted = formatted.replace('.0', '');
    return amount >= 0 ? `$${formatted}K` : `($${formatted}K)`;
  }
  // Normal values
  return amount >= 0 ? `$${formatValue(amount, 0)}` : `($${formatValue(Math.abs(amount), 0)})`;
};

export const formatAmount = (
  amount: number | null,
  decimals: number = 0,
  compact: boolean = false,
  unitless: boolean = false
) => {
  if (amount === null) return ' ';
  if (amount === 0) return compact ? '' : ' -- ';
  const units = unitless ? '' : compact ? '$' : '$ ';
  if (amount < 0) {
    return unitless ? formatValue(amount, decimals) : `(${units}${formatValue(Math.abs(amount), decimals)})`;
  }
  return `${units}${formatValue(amount, decimals)}`;
};

export const FormatActual = (
  value?: number | null,
  total?: boolean,
  small?: boolean,
  showJustDots?: boolean,
  auditResult?: AuditResult | undefined,
  transactionCount?: number,
  cb?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
) => {
  if (!value || value === 0)
    return (
      <div className={prefix('value')}>
        <div onClick={cb} className={classNames(prefix(), 'empty', { 'has-callback': cb !== undefined })}>
          <div className={prefix('value-empty')}>--</div>
        </div>
        {showJustDots && transactionCount === 0 && (
          <Whisper placement="top" trigger="hover" speaker={<Tooltip>{auditResult?.message}</Tooltip>}>
            <div className={classNames(prefix(), 'small-dot', [auditResult?.type.toLocaleLowerCase()])}></div>
          </Whisper>
        )}
      </div>
    );
  if (value < 0) {
    return (
      <div className={classNames(prefix('value'), { total: total, small: small })}>
        <div onClick={cb} className={classNames(prefix(), { 'has-callback': cb !== undefined })}>
          (<span>$</span>
          {formatValue(Math.abs(value))})
        </div>
      </div>
    );
  }
  return (
    <div className={classNames(prefix('value'), { total: total, small: small })}>
      <div onClick={cb} className={classNames(prefix(), { 'has-callback': cb !== undefined })}>
        <span>$</span>
        {formatValue(value)}
      </div>
    </div>
  );
};

export const FormatPercentage = (
  value?: number | null,
  total?: boolean,
  showJustDots?: boolean,
  auditResult?: AuditResult | undefined,
  cb?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void,
  forceZero?: boolean
) => {
  const isNullish = value == null;
  const isDefaultOne = !forceZero && value === 1;

  // Case 1: Show dots when applicable
  if ((isNullish || isDefaultOne) && showJustDots) {
    return (
      <Whisper placement="top" trigger="hover" speaker={<Tooltip>{auditResult?.message}</Tooltip>}>
        <div onClick={cb} className={classNames(prefix(), 'small-dot', { 'has-callback': cb !== undefined })}></div>
      </Whisper>
    );
  }

  // Case 2: Value is null/undefined (and NOT showJustDots)
  if (isNullish) {
    return (
      <div className={prefix('percentage')}>
        <div onClick={cb} className={classNames(prefix(), { 'has-callback': cb !== undefined })}>
          <div className={prefix('percentage-empty')}>--</div>
        </div>
      </div>
    );
  }

  // Caso 3: Negative Value
  const perc = value * 100;
  if (value < 0) {
    return (
      <div className={classNames(prefix('percentage'), { total: total })}>
        <div onClick={cb} className={classNames(prefix(), { 'has-callback': cb !== undefined })}>
          -{formatValue(Math.abs(perc), 2)}
          <span>%</span>
        </div>
      </div>
    );
  }

  // Case 4: Value (>= 0)
  return (
    <div className={classNames(prefix('percentage'), { total: total })}>
      <div onClick={cb} className={classNames(prefix(), { 'has-callback': cb !== undefined })}>
        {value === 0 ? '0.00' : formatValue(perc, 2)}
        <span>%</span>
      </div>
    </div>
  );
};

export const FormatTransactionNumbers = (
  transactionCount?: number | null,
  total?: boolean,
  showJustDots?: boolean,
  auditResult?: AuditResult | undefined,
  cb?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
) => {
  if (!transactionCount || transactionCount === 0)
    return (
      <div className={prefix('transaction-numbers')}>
        <div onClick={cb} className={classNames(prefix(), { 'has-callback': cb !== undefined })}>
          <div className={prefix('transaction-numbers-empty')}>0</div>
        </div>
      </div>
    );
  return (
    <div className={classNames(prefix('transaction-numbers'), { total: total })}>
      {showJustDots ? (
        <Whisper placement="top" trigger="hover" speaker={<Tooltip>{auditResult?.message}</Tooltip>}>
          <div onClick={cb} className={classNames(prefix(), 'small-dot', { 'has-callback': cb !== undefined })}></div>
        </Whisper>
      ) : (
        <Whisper placement="top" trigger="hover" speaker={<Tooltip>{auditResult?.message}</Tooltip>}>
          <div onClick={cb} className={classNames(prefix(), { 'has-callback': cb !== undefined })}>
            {formatValue(transactionCount)}
          </div>
        </Whisper>
      )}
    </div>
  );
};
